import { kebabCase } from 'lodash-es';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { ComputedRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSkForm } from '@silae/components/form';
import { SkSelectProps } from '@silae/components/inputs';
import { useLocale } from '@silae/composables/i18n';
import { ReportType } from '~/services';
import { useAuthStore } from '~/stores';
import { generateYearMonthItems } from '~/utils';

export type BillingActivityReportForm = {
	startPeriod: string;
	endPeriod: string;
	types: ReportType[];
};

export function useBillingActivityReportForm(loading: ComputedRef<boolean>) {
	const { t } = useI18n();
	const { locale } = useLocale();

	const authStore = useAuthStore();
	const { userPartnerCreationDate } = storeToRefs(authStore);

	const { handleSubmit, resetForm, defineSkField } = useSkForm<BillingActivityReportForm>();

	const typesItems = computed(() =>
		Object.values(ReportType).map((reportType: ReportType) => ({
			label: t(`billing.report-type-enum.${kebabCase(reportType)}`),
			value: reportType
		}))
	);

	const [typesValue, typesProps] = defineSkField('types')<SkSelectProps>(
		{
			label: computed(() => t('billing.report-type')),
			placeholder: computed(() => t('billing.report-type')),
			disabled: loading,
			clearable: computed(() => !loading.value),
			attach: true,
			multiple: true,
			items: typesItems
		},
		'required'
	);

	const [startPeriodValue, startPeriodProps] = defineSkField('startPeriod')<SkSelectProps>(
		{
			label: computed(() => t('billing.start-date')),
			disabled: loading,
			clearable: computed(() => !loading.value),
			attach: true,
			placeholder: computed(() => t('billing.start-date'))
		},
		'required'
	);

	const [endPeriodValue, endPeriodProps] = defineSkField('endPeriod')<SkSelectProps>(
		{
			label: computed(() => t('billing.end-date')),
			disabled: loading,
			clearable: computed(() => !loading.value),
			attach: true,
			placeholder: computed(() => t('billing.end-date'))
		},
		'required'
	);

	const startPeriodItems = computed(() =>
		generateYearMonthItems(userPartnerCreationDate.value, locale.value, (year: number, month: number) => {
			if (!endPeriodValue.value) {
				return false;
			}

			const endPeriodDate = DateTime.fromFormat(endPeriodValue.value, 'yyyy-MM');
			const endPeriodMonth = endPeriodDate.month;
			const endPeriodYear = endPeriodDate.year;
			return (endPeriodMonth < month && endPeriodYear === year) || endPeriodYear < year;
		})
	);

	const endPeriodItems = computed(() =>
		generateYearMonthItems(userPartnerCreationDate.value, locale.value, (year: number, month: number) => {
			if (!startPeriodValue.value) {
				return false;
			}

			const startPeriodDate = DateTime.fromFormat(startPeriodValue.value, 'yyyy-MM');
			const startPeriodMonth = startPeriodDate.month;
			const startPeriodYear = startPeriodDate.year;
			return (startPeriodMonth > month && startPeriodYear === year) || startPeriodYear > year;
		})
	);

	return {
		/** Refs and computed */
		startPeriodValue,
		startPeriodProps,
		startPeriodItems,
		endPeriodValue,
		endPeriodProps,
		endPeriodItems,
		typesValue,
		typesProps,

		/** Functions */
		handleSubmit,
		resetForm
	};
}
